import React from "react"
import Layout from "../components/Layout"
import { SEO } from "../components/SEO/SEO"
import { PageHero } from "../components/programmatic/PageHero"
import { FAQBlock } from "../components/programmatic/FAQBlock"
import { ServiceCTA } from "../components/programmatic/ServiceCTA"
import { RelatedLinks } from "../components/programmatic/RelatedLinks"
import { Breadcrumbs } from "../components/programmatic/Breadcrumbs"
import { AuthorBox } from "../components/programmatic/AuthorBox"
import { ComparisonTable } from "../components/programmatic/ComparisonTable"
import { Markdown } from "../components/programmatic/Markdown"
import { buildLocalBusiness } from "../components/SEO/schema/localBusiness"
import { buildArticle } from "../components/SEO/schema/article"
import { buildFaqPage } from "../components/SEO/schema/faqPage"
import { buildBreadcrumbList } from "../components/SEO/schema/breadcrumbList"
import type { Comparison } from "../data/types"

type Props = {
  pageContext: Comparison
}

const ComparisonTemplate = ({ pageContext }: Props) => {
  const path = `/comparacoes/${pageContext.slug}`
  const headline = `${pageContext.aLabel} vs ${pageContext.bLabel}: qual escolher?`
  const crumbs = [
    { name: "Início", to: "/" },
    { name: "Comparações", to: "/comparacoes" },
    { name: `${pageContext.aLabel} vs ${pageContext.bLabel}`, to: path },
  ]

  const jsonLd = [
    buildLocalBusiness(),
    buildArticle({
      headline,
      description: pageContext.metaDescription,
      url: path,
    }),
    buildFaqPage(pageContext.faq),
    buildBreadcrumbList(crumbs),
  ]

  return (
    <Layout>
      <SEO
        title={pageContext.metaTitle}
        description={pageContext.metaDescription}
        canonicalPath={path}
        jsonLd={jsonLd}
      />
      <Breadcrumbs crumbs={crumbs} />
      <PageHero
        eyebrow="Comparação"
        headline={headline}
        intro={pageContext.summary}
      />
      <ComparisonTable
        rows={pageContext.rows}
        aLabel={pageContext.aLabel}
        bLabel={pageContext.bLabel}
      />
      <section className="text-gray-700 body-font">
        <div className="container mx-auto px-5 py-10">
          <div className="max-w-3xl space-y-10">
            <article>
              <h2 className="title-font text-2xl md:text-3xl font-medium text-gray-900 mb-4">
                Quando escolher {pageContext.aLabel}
              </h2>
              <div className="text-base md:text-lg leading-relaxed text-gray-700">
                <Markdown source={pageContext.whenA} />
              </div>
            </article>
            <article>
              <h2 className="title-font text-2xl md:text-3xl font-medium text-gray-900 mb-4">
                Quando escolher {pageContext.bLabel}
              </h2>
              <div className="text-base md:text-lg leading-relaxed text-gray-700">
                <Markdown source={pageContext.whenB} />
              </div>
            </article>
            <article>
              <h2 className="title-font text-2xl md:text-3xl font-medium text-gray-900 mb-4">
                Posso combinar os dois?
              </h2>
              <div className="text-base md:text-lg leading-relaxed text-gray-700">
                <Markdown source={pageContext.combine} />
              </div>
            </article>
            <article className="bg-purple-50 rounded p-6">
              <h2 className="title-font text-xl font-medium text-gray-900 mb-3">
                Da minha prática
              </h2>
              <div className="text-base leading-relaxed text-gray-700">
                <Markdown source={pageContext.gabrielaTake} />
              </div>
            </article>
          </div>
        </div>
      </section>
      <ServiceCTA prefill={pageContext.ctaPrefill} />
      <FAQBlock faq={pageContext.faq} />
      <RelatedLinks links={pageContext.related} />
      <AuthorBox />
    </Layout>
  )
}

export default ComparisonTemplate
